
import { Product } from '@/types';
import { apiService } from './apiService';
import { API_CONFIG } from '@/config/apiConfig';
import { generateId, storeProducts } from './storageService';
import * as indexedDBService from './indexedDBService';

// Fetch all products from API
export const fetchProducts = async (): Promise<Product[]> => {
  try {
    if (!API_CONFIG.baseURL) {
      console.log('No API URL configured, returning mock products');
      // Return mock products for development
      return getMockProducts();
    }

    console.log('Fetching products from API:', `${API_CONFIG.baseURL}${API_CONFIG.endpoints.products.list}`);
    const url = API_CONFIG.endpoints.products.list;
    const response = await apiService.get<any[]>(url);
    
    console.log(`Fetched ${response?.length || 0} products from API`);
    
    if (!response || !Array.isArray(response)) {
      console.warn('Invalid response format for products:', response);
      return getMockProducts();
    }
    
    // Map the API response to our Product type
    const mappedProducts = response.map(item => {
      // Debug logging to see the actual structure of the first item
      if (response.indexOf(item) === 0) {
        console.log('Sample product from API:', item);
      }
      
      // Check if API is returning the expected field structure
      const hasUpperCaseFields = item.ID !== undefined && item.Nome !== undefined;
      
      // Enhanced ID handling to ensure we always have a valid ID
      let productId: string = '';
      
      if (hasUpperCaseFields) {
        // First try to use ID field
        if (item.ID && !isNaN(parseInt(String(item.ID), 10))) {
          productId = String(item.ID);
        } 
        // Then try ProdutoID field if ID is not valid
        else if (item.ProdutoID && !isNaN(parseInt(String(item.ProdutoID), 10))) {
          productId = String(item.ProdutoID);
        } 
        // Generate id only as last resort
        else {
          productId = generateId();
          console.warn(`Generated random ID for product "${item.Nome}" because API didn't provide a valid ID`);
        }
        
        // Log for debugging
        console.log(`Product ${item.Nome}: Original ID=${item.ID || item.ProdutoID}, Stored ID=${productId}`);
      } else {
        // For non-uppercase fields format
        if (item.id && !isNaN(parseInt(String(item.id), 10))) {
          productId = String(item.id);
        } else {
          productId = generateId();
          console.warn(`Generated random ID for product "${item.name}" because API didn't provide a valid ID`);
        }
      }
      
      return {
        id: productId,
        name: hasUpperCaseFields ? item.Nome : (item.name || 'Unknown Product'),
        description: hasUpperCaseFields ? `${item.Marca || ''} ${item.Codigo || ''}`.trim() : (item.description || ''),
        price: hasUpperCaseFields ? Number(item.Preco || 0) : (item.price || 0),
        categoryId: hasUpperCaseFields ? String(item.CategoriaID || '') : (item.categoryId || ''),
        image: hasUpperCaseFields ? (item.Imagem || '/placeholder.svg') : (item.image || '/placeholder.svg'),
        available: hasUpperCaseFields ? (item.Estoque > 0) : (item.available !== undefined ? item.available : true),
        // Additional fields if needed
        code: hasUpperCaseFields ? item.Codigo : (item.code || ''),
        brand: hasUpperCaseFields ? item.Marca : (item.brand || ''),
        stock: hasUpperCaseFields ? Number(item.Estoque || 0) : (item.stock || 0)
      };
    });
    
    console.log(`Mapped ${mappedProducts.length} products to correct format`);
    // Log a few products for debugging
    if (mappedProducts.length > 0) {
      console.log('First 3 products after mapping:');
      mappedProducts.slice(0, 3).forEach(p => {
        console.log(`- ${p.name} (ID: ${p.id})`);
      });
    }
    
    return mappedProducts;
  } catch (error) {
    console.error('Error fetching products:', error);
    return getMockProducts();
  }
};

// Fetch a specific product by ID
export const fetchProductByIdFromApi = async (id: string): Promise<Product | null> => {
  try {
    if (!API_CONFIG.baseURL) {
      // For development, return a mock product if no API URL is configured
      const mockProducts = getMockProducts();
      return mockProducts.find(p => p.id === id) || null;
    }

    const url = API_CONFIG.endpoints.products.byId.replace(':id', id);
    const response = await apiService.get<any>(url);
    
    // Ensure the product has an ID and map to our Product type
    if (response) {
      const hasUpperCaseFields = response.ID !== undefined && response.Nome !== undefined;
      
      return {
        id: hasUpperCaseFields ? String(response.ID || response.ProdutoID) : (response.id || id),
        name: hasUpperCaseFields ? response.Nome : (response.name || 'Unknown Product'),
        description: hasUpperCaseFields ? `${response.Marca || ''} ${response.Codigo || ''}`.trim() : (response.description || ''),
        price: hasUpperCaseFields ? Number(response.Preco || 0) : (response.price || 0),
        categoryId: hasUpperCaseFields ? String(response.CategoriaID || '') : (response.categoryId || ''),
        image: hasUpperCaseFields ? (response.Imagem || '/placeholder.svg') : (response.image || '/placeholder.svg'),
        available: hasUpperCaseFields ? (response.Estoque > 0) : (response.available !== undefined ? response.available : true),
        code: hasUpperCaseFields ? response.Codigo : (response.code || ''),
        brand: hasUpperCaseFields ? response.Marca : (response.brand || ''),
        stock: hasUpperCaseFields ? Number(response.Estoque || 0) : (response.stock || 0)
      };
    }
    
    return null;
  } catch (error) {
    console.error(`Error fetching product with ID ${id}:`, error);
    return null;
  }
};

// Sync products with API and store locally
export const syncProductsWithApi = async (): Promise<boolean> => {
  try {
    console.log('Starting product synchronization process...');
    const products = await fetchProducts();
    
    if (products && products.length > 0) {
      console.log(`Successfully fetched ${products.length} products, storing them...`);
      
      // Clear existing products before storing new ones to avoid duplication
      try {
        await indexedDBService.clearStore('products');
        console.log('Cleared existing products before syncing');
        
        await storeProducts(products);
        console.log(`Successfully stored ${products.length} products`);
        return true;
      } catch (storageError) {
        console.error('Error saving products:', storageError);
        return false;
      }
    }
    console.log('No products fetched or empty products array');
    return false;
  } catch (error) {
    console.error('Error syncing products with API:', error);
    return false;
  }
};

// Create sample products for development/testing
const getMockProducts = (): Product[] => {
  return [
    {
      id: generateId(),
      name: 'Café Expresso',
      description: 'Café expresso tradicional',
      price: 4.5,
      categoryId: '1',
      image: '/images/espresso.jpg',
      available: true
    },
    {
      id: generateId(),
      name: 'Cappuccino',
      description: 'Café com leite e chocolate',
      price: 7.0,
      categoryId: '1',
      image: '/images/cappuccino.jpg',
      available: true
    },
    {
      id: generateId(),
      name: 'Sanduíche Natural',
      description: 'Pão integral, frango, alface e tomate',
      price: 12.5,
      categoryId: '2',
      image: '/images/sandwich.jpg',
      available: true
    },
    {
      id: generateId(),
      name: 'Água Mineral',
      description: 'Água mineral sem gás 500ml',
      price: 3.0,
      categoryId: '1',
      image: '/images/water.jpg',
      available: true
    },
    {
      id: generateId(),
      name: 'Bolo de Chocolate',
      description: 'Fatia de bolo de chocolate com cobertura',
      price: 8.0,
      categoryId: '4',
      image: '/images/chocolate_cake.jpg',
      available: true
    }
  ];
};
